import { useGlobalMutation } from 'state/query-client/hooks';
import {
  ActivateTelegramConnectionResponse,
  ActivateWhatsAppConnectionResponse,
  ActivateTeamsConnectionResponse,
  CompleteConnectionResponse,
  ConfigureTelegramConnectionRequest,
  CreateIntegrationConnectionRequest,
  activateTelegramConnection,
  activateWhatsAppConnection,
  activateTeamsConnection,
  bindWhatsAppConnection,
  ConfigureWhatsAppConnectionResponse,
  ConfigureTeamsConnectionResponse,
  configureTelegramConnection,
  configureWhatsAppConnection,
  configureTeamsConnection,
  createIntegrationConnection,
  deactivateTelegramConnection,
  deactivateWhatsAppConnection,
  deactivateTeamsConnection,
} from 'services/integration-service';
import { useConfig } from './use-config';

export const useCreateIntegrationConnection = () => {
  const { configurationSettings } = useConfig();

  return useGlobalMutation<CompleteConnectionResponse, Error, CreateIntegrationConnectionRequest>({
    mutationFn: async (payload) => {
      if (!configurationSettings) {
        throw new Error('Configuration settings are not available');
      }

      return createIntegrationConnection(configurationSettings, payload);
    },
  });
};

type ConnectTelegramIntegrationParams = {
  agentId: string;
  botToken: string;
};

export const useConnectTelegramIntegration = () => {
  const { configurationSettings } = useConfig();

  return useGlobalMutation<ActivateTelegramConnectionResponse, Error, ConnectTelegramIntegrationParams>({
    mutationFn: async ({ agentId, botToken }) => {
      if (!configurationSettings) {
        throw new Error('Configuration settings are not available');
      }

      const connection = await createIntegrationConnection(configurationSettings, {
        channel_id: 'telegram',
        owner_type: 'organization',
        display_name: 'Telegram bot',
        auth_type: 'api_key',
      });

      await configureTelegramConnection(configurationSettings, connection.id, {
        agent_id: agentId,
        bot_token: botToken,
      });

      return activateTelegramConnection(configurationSettings, connection.id);
    },
  });
};

type ConnectWhatsAppIntegrationParams = {
  agentId: string;
  appId: string;
  wabaId: string;
  phoneNumberId: string;
  accessToken: string;
  appSecret: string;
  verifyToken: string;
};

export const useConnectWhatsAppIntegration = () => {
  const { configurationSettings } = useConfig();

  return useGlobalMutation<
    ConfigureWhatsAppConnectionResponse,
    Error,
    ConnectWhatsAppIntegrationParams
  >({
    mutationFn: async ({
      agentId,
      appId,
      wabaId,
      phoneNumberId,
      accessToken,
      appSecret,
      verifyToken,
    }) => {
      if (!configurationSettings) {
        throw new Error('Configuration settings are not available');
      }

      const connection = await createIntegrationConnection(configurationSettings, {
        channel_id: 'whatsapp',
        owner_type: 'organization',
        display_name: 'Customer Support WhatsApp',
        auth_type: 'api_key',
      });

      const configured = await configureWhatsAppConnection(configurationSettings, connection.id, {
        agent_id: agentId,
        app_id: appId,
        waba_id: wabaId,
        phone_number_id: phoneNumberId,
        access_token: accessToken,
        app_secret: appSecret,
        webhook_verify_token: verifyToken,
      });

      return {
        ...configured,
        connection_id: configured.connection_id ?? connection.id,
      };
    },
  });
};

export type { ConfigureTelegramConnectionRequest, CreateIntegrationConnectionRequest };

export const useActivateTelegramIntegration = () => {
  const { configurationSettings } = useConfig();

  return useGlobalMutation<ActivateTelegramConnectionResponse, Error, string>({
    mutationFn: async (connectionId) => {
      if (!configurationSettings) {
        throw new Error('Configuration settings are not available');
      }

      return activateTelegramConnection(configurationSettings, connectionId);
    },
  });
};

export const useActivateWhatsAppIntegration = () => {
  const { configurationSettings } = useConfig();

  return useGlobalMutation<
    ActivateWhatsAppConnectionResponse,
    Error,
    { connectionId: string; agentId: string }
  >({
    mutationFn: async ({ connectionId, agentId }) => {
      if (!configurationSettings) {
        throw new Error('Configuration settings are not available');
      }

      await bindWhatsAppConnection(configurationSettings, connectionId, agentId);
      return activateWhatsAppConnection(configurationSettings, connectionId);
    },
  });
};

export const useDeactivateTelegramIntegration = () => {
  const { configurationSettings } = useConfig();

  return useGlobalMutation<CompleteConnectionResponse, Error, string>({
    mutationFn: async (connectionId) => {
      if (!configurationSettings) {
        throw new Error('Configuration settings are not available');
      }

      return deactivateTelegramConnection(configurationSettings, connectionId);
    },
  });
};

export const useDeactivateWhatsAppIntegration = () => {
  const { configurationSettings } = useConfig();

  return useGlobalMutation<CompleteConnectionResponse, Error, string>({
    mutationFn: async (connectionId) => {
      if (!configurationSettings) {
        throw new Error('Configuration settings are not available');
      }

      return deactivateWhatsAppConnection(configurationSettings, connectionId);
    },
  });
};

export type ConnectTeamsIntegrationParams = {
  agentId: string;
  azureTenantId: string;
  microsoftAppId: string;
  clientSecret: string;
  clientSecretExpiresAt: string;
  botName: string;
  azureBotResourceName: string;
};

export const useConnectTeamsIntegration = () => {
  const { configurationSettings } = useConfig();

  return useGlobalMutation<
    ConfigureTeamsConnectionResponse,
    Error,
    ConnectTeamsIntegrationParams
  >({
    mutationFn: async ({
      agentId,
      azureTenantId,
      microsoftAppId,
      clientSecret,
      clientSecretExpiresAt,
      botName,
      azureBotResourceName,
    }) => {
      if (!configurationSettings) {
        throw new Error('Configuration settings are not available');
      }

      const connection = await createIntegrationConnection(configurationSettings, {
        channel_id: 'teams',
        owner_type: 'organization',
        display_name: botName || 'Customer Support Teams Bot',
        auth_type: 'api_key',
      });

      const configured = await configureTeamsConnection(configurationSettings, connection.id, {
        agent_id: agentId,
        azure_tenant_id: azureTenantId,
        microsoft_app_id: microsoftAppId,
        client_secret: clientSecret,
        client_secret_expires_at: clientSecretExpiresAt,
        bot_name: botName,
        azure_bot_resource_name: azureBotResourceName,
      });

      const url =
        (configured as any)?.webhook_url ||
        (configured as any)?.webhookUrl ||
        (configured as any)?.messaging_endpoint ||
        (configured as any)?.messaging_endpoint_url ||
        (configured as any)?.endpoint_url ||
        (configured as any)?.endpointUrl ||
        (configured as any)?.connection_url ||
        (configured as any)?.connectionUrl ||
        (configured as any)?.url ||
        (configured as any)?.bot_link ||
        (configured as any)?.data?.webhook_url ||
        (configured as any)?.data?.webhookUrl ||
        (configured as any)?.data?.messaging_endpoint ||
        (configured as any)?.data?.endpoint_url ||
        (configured as any)?.data?.url ||
        (configured as any)?.external?.webhook_url ||
        (configured as any)?.external_refs?.teams?.webhook_url ||
        (configured as any)?.external_refs?.teams?.messaging_endpoint ||
        '';

      const connId =
        (configured as any)?.connection_id ?? (configured as any)?.id ?? connection.id;

      return {
        ...configured,
        connection_id: connId,
        webhook_url: url,
        webhookUrl: url,
      };
    },
  });
};

export const useActivateTeamsIntegration = () => {
  const { configurationSettings } = useConfig();

  return useGlobalMutation<ActivateTeamsConnectionResponse, Error, string>({
    mutationFn: async (connectionId) => {
      if (!configurationSettings) {
        throw new Error('Configuration settings are not available');
      }

      return activateTeamsConnection(configurationSettings, connectionId);
    },
  });
};

export const useDeactivateTeamsIntegration = () => {
  const { configurationSettings } = useConfig();

  return useGlobalMutation<CompleteConnectionResponse, Error, string>({
    mutationFn: async (connectionId) => {
      if (!configurationSettings) {
        throw new Error('Configuration settings are not available');
      }

      return deactivateTeamsConnection(configurationSettings, connectionId);
    },
  });
};
